/**
 * Classifies `getSalaryDisbursementFile`'s raw server-side `BAD_REQUEST`
 * message (`apps/api/src/trpc/routers/payroll.ts`) into a known failure
 * class, so `DisbursementSection` can render a translated, locale-aware
 * message instead of the raw English string verbatim.
 *
 * Adversarial-review finding (C8 second round, #22410): `post_run_sections
 * .tsx`'s disbursement-file error handling previously called
 * `setError(result.message)` directly, rendering the server's raw English
 * message to every locale, including si/ta/id/fr users — this PR already
 * fixed the exact same leak class for the statutory-payments "not available
 * yet" reason (`statutory_filing_not_available`), so leaving this path
 * un-fixed would be inconsistent within the same PR.
 *
 * Split out as its own pure, co-located file rather than added to
 * `post_run_actions_calculations.ts`, which must stay byte-for-byte
 * unchanged per #22330's own acceptance criteria — same rationale as
 * `post_run_payment_source_picker.ts`'s module docblock.
 *
 * Matches on stable substrings/prefixes of the server's literal messages
 * rather than full equality where the message embeds a variable (the pay
 * run's current `status`), so a `status` value this classifier hasn't
 * explicitly enumerated still matches the right class. Any message that
 * doesn't match a known class falls back to `'unknown'` — the caller renders
 * a generic translated "something went wrong" message for that case, never
 * the raw string. This is a best-effort classification, not an exhaustive
 * mirror of every possible server error: a future new `BAD_REQUEST` message
 * on the server simply falls into `'unknown'` until someone adds a class for
 * it, rather than leaking English in the meantime.
 */
export type DisbursementErrorClass =
  | 'not_approved'
  | 'no_lines'
  | 'no_bank_details'
  | 'not_found'
  | 'unknown';

export function classify_disbursement_error_message(
  message: string,
): DisbursementErrorClass {
  if (/can only be generated for approved pay runs/i.test(message)) {
    return 'not_approved';
  }
  if (message === 'This pay run has no pay-run lines to disburse') {
    return 'no_lines';
  }
  if (
    message.startsWith(
      'No employees in this pay run have complete bank details on file',
    )
  ) {
    return 'no_bank_details';
  }
  if (message === 'Pay run not found') {
    return 'not_found';
  }
  return 'unknown';
}
